/* ------------------------------------------------------------------ */
/*  Shared types for the Botilia Search frontend                      */
/* ------------------------------------------------------------------ */

/** A single product returned by the search API */
export interface Product {
  id: string;
  name: string;
  nameEl?: string;
  url: string;
  imageUrl: string;
  price: number;
  salePrice?: number;
  producer?: string;
  region?: string;
  wineType?: WineType;
  grape?: string;
  /** Tasting attributes (discrete descriptors) + food pairing text */
  body?: string;
  acidity?: string;
  tannins?: string;
  fruitiness?: string;
  foodPairing?: string;
  year?: number;
  rating?: number;
  reviewCount?: number;
  inStock: boolean;
  isBestSeller?: boolean;
  isPromo?: boolean;
  description?: string;
  categories?: string[];
}

export type WineType =
  | 'red'
  | 'white'
  | 'rose'
  | 'sparkling'
  | 'dessert'
  | 'fortified'
  | 'orange';

/** A blog article returned by the search API */
export interface Article {
  id: string;
  title: string;
  url: string;
  excerpt?: string;
  imageUrl?: string;
  publishedAt: string;
  category?: string;
  author?: string;
}

/** A generic page result (not product, not article) */
export interface PageResult {
  id: string;
  title: string;
  url: string;
  excerpt: string;
  type: string;
}

/** Parameters for the search API call */
export interface SearchParams {
  /** Search exactly what was typed, without spell correction. */
  exact?: boolean;
  q?: string;
  category?: string;
  type?: string;
  country?: string;
  region?: string;
  grape?: string;
  producer?: string;
  subcategory?: string;
  vintage?: string;
  tags?: string;
  body?: string;
  acidity?: string;
  tannins?: string;
  fruitiness?: string;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  sort?: 'relevance' | 'price_asc' | 'price_desc' | 'newest' | 'rating';
  page?: number;
  limit?: number;
  offset?: number;
  /** Session ID for anonymous personalization */
  sessionId?: string;
  /** Language partition to search in */
  lang?: string;
}

/** Admin-managed autocomplete keyword with boost weight */
export interface AutocompleteKeyword {
  keyword: string;
  boost: number;
  enabled: boolean;
}

/** AI context hint for intent-based queries (pairing, occasion, etc.) */
export interface AIContext {
  type: 'pairing' | 'occasion' | 'price' | 'category' | 'info' | 'general';
  message: string;
  messageEn?: string;
  food?: string;
  occasion?: string;
  suggestedCategories: string[];
  /** AI-enriched fields (only present when OpenAI generates the context) */
  reasoning?: string;
  reasoningEn?: string;
  extractedTerms?: string[];
  aiConfidence?: number;
  fromCache?: boolean;
  source?: 'ai' | 'rules' | 'pending';
  wineCharacteristics?: string[];
  suggestedGrapes?: string[];
  servingTemp?: string;
  suggestedPriceRange?: { min?: number; max?: number };
  wineRecommendations?: Array<{
    grape: string;
    description: string;
    category?: string;
  }>;
}

/** A curated collection/category imported by a category-type feed (crawled or JSON). */
export interface CollectionCategory {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  url?: string;
}

export interface SearchResponse {
  products: Product[];
  articles: Article[];
  pages: PageResult[];
  total: number;
  query: string;
  /** The results below are for this corrected query. */
  correctedQuery?: string;
  /** A correction we are not sure of: offered, results unchanged. */
  didYouMean?: string;
  suggestions: string[];
  facets?: Facets;
  took: number;
  aiContext?: AIContext;
  /** Imported collections whose name/description matches the query */
  collections?: CollectionCategory[];
}

/** Facets computed from search results — dynamic keys based on product fields */
export interface Facets {
  [key: string]: FacetBucket[] | { min: number; max: number };
  priceRange: { min: number; max: number };
}

export interface FacetBucket {
  value: string;
  count: number;
  label: string;
}

/** Response from the defaults endpoint (empty-query state) */
export interface DefaultsResponse {
  bestSellers: Product[];
  promos: Product[];
  popularSearches: string[];
  trendingSearches?: { query: string; resolvedTo?: string }[];
}

/** Admin configuration object */
export interface AdminConfig {
  boostWeights: {
    nameBoost: number;
    descriptionBoost: number;
    categoryBoost: number;
    producerBoost: number;
    bestSellerBoost: number;
    promoBoost: number;
    inStockBoost: number;
  };
  defaults: {
    maxBestSellers: number;
    maxPromos: number;
    maxPopularSearches: number;
  };
  synonyms: SynonymGroup[];
  knowledge: KnowledgeEntry[];
}

export interface SynonymGroup {
  id: string;
  terms: string[];
  description?: string;
}

export interface KnowledgeEntry {
  id: string;
  trigger: string;
  response: string;
  priority: number;
}

/** Analytics data */
export interface AnalyticsSummary {
  totalSearches: number;
  uniqueQueries: number;
  avgQueryTimeMs: number;
  zeroResultRate: number;
  totalClicks: number;
  overallCtr: number;
}

export interface QueryWithCtr {
  query: string;
  searches: number;
  clicks: number;
  ctr: number;
}

export interface DailyVolume {
  date: string;
  totalSearches: number;
  uniqueQueries: number;
  avgQueryTimeMs: number;
  zeroResultCount: number;
}

export interface Analytics {
  topSearches: Array<{ query: string; count: number }>;
  searchVolume: Array<{ date: string; count: number }>;
  zeroResultQueries: Array<{ query: string; count: number }>;
  abandonedSearches: Array<{ query: string; count: number; avgResultCount: number }>;
  totalSearches: number;
  averageResponseTime: number;
  summary: AnalyticsSummary;
  topQueriesWithCtr: QueryWithCtr[];
  dailyVolume: DailyVolume[];
}

/* ------------------------------------------------------------------ */
/*  Semantic / AI Search                                               */
/* ------------------------------------------------------------------ */

/** Configuration for AI semantic search */
export interface SemanticSearchConfig {
  enabled: boolean;
  vectorWeight: number;
  similarityThreshold: number;
  minWordCount: number;
  model: string;
  dimensions: number;
}

/** Status info for the embedding index */
export interface SemanticSearchStatus {
  apiKeyConfigured: boolean;
  enabled: boolean;
  model: string;
  vectorWeight: number;
  similarityThreshold: number;
  minWordCount: number;
  indexedCount: number;
  totalProducts: number;
  lastReindexAt: string | null;
  isReindexing: boolean;
}

/** AI Context configuration (sommelier-style answers) */
export interface AIContextConfig {
  enabled: boolean;
  model: string;
  minWordCount: number;
  cacheTTLDays: number;
  similarityThreshold: number;
  maxTokens: number;
  timeoutMs: number;
  tone?: string;
  responseStyle?: string;
}

/** System status */
export interface SystemStatus {
  indexSize: number;
  lastSync: string | null;
  lastCrawl: string | null;
  uptime: number;
  productCount: number;
  articleCount: number;
  health: 'healthy' | 'degraded' | 'down';
}

/** The state managed by the useSearch hook */
export interface SearchState {
  query: string;
  debouncedQuery: string;
  results: SearchResponse | null;
  defaults: DefaultsResponse | null;
  autocomplete: string[];
  isLoading: boolean;
  isLoadingDefaults: boolean;
  error: string | null;
  isOpen: boolean;
  activeIndex: number;
  recentSearches: string[];
  filters: SearchFilters;
}

export interface SearchFilters {
  category?: string;
  type?: string;
  country?: string;
  region?: string;
  grape?: string;
  producer?: string;
  subcategory?: string;
  vintage?: string;
  tags?: string;
  body?: string;
  acidity?: string;
  tannins?: string;
  fruitiness?: string;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  sort?: SearchParams['sort'];
  limit?: number;
  /** Allow additional dynamic filter fields from feed data */
  [key: string]: string | number | boolean | undefined;
}

/** Wine type metadata for display */
export const WINE_TYPE_META: Record<
  WineType,
  { label: string; labelEn: string; labelKey: string; color: string; bgColor: string }
> = {
  red: {
    label: 'Κόκκινο',
    labelEn: 'Red',
    labelKey: 'wineType.red',
    color: 'text-red-800',
    bgColor: 'bg-red-100',
  },
  white: {
    label: 'Λευκό',
    labelEn: 'White',
    labelKey: 'wineType.white',
    color: 'text-amber-800',
    bgColor: 'bg-amber-50',
  },
  rose: {
    label: 'Ροζέ',
    labelEn: 'Rosé',
    labelKey: 'wineType.rose',
    color: 'text-pink-800',
    bgColor: 'bg-pink-100',
  },
  sparkling: {
    label: 'Αφρώδες',
    labelEn: 'Sparkling',
    labelKey: 'wineType.sparkling',
    color: 'text-yellow-800',
    bgColor: 'bg-yellow-100',
  },
  dessert: {
    label: 'Γλυκό',
    labelEn: 'Dessert',
    labelKey: 'wineType.dessert',
    color: 'text-orange-800',
    bgColor: 'bg-orange-100',
  },
  fortified: {
    label: 'Ενισχυμένο',
    labelEn: 'Fortified',
    labelKey: 'wineType.fortified',
    color: 'text-purple-800',
    bgColor: 'bg-purple-100',
  },
  orange: {
    label: 'Πορτοκαλί',
    labelEn: 'Orange',
    labelKey: 'wineType.orange',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
  },
};

/** Category filter options */
export const CATEGORY_OPTIONS = [
  { value: '', label: 'Όλες οι κατηγορίες', labelEn: 'All categories', labelKey: 'filter.allCategories' },
  { value: 'red', label: 'Κόκκινο', labelEn: 'Red', labelKey: 'wineType.red' },
  { value: 'white', label: 'Λευκό', labelEn: 'White', labelKey: 'wineType.white' },
  { value: 'rose', label: 'Ροζέ', labelEn: 'Rosé', labelKey: 'wineType.rose' },
  { value: 'sparkling', label: 'Αφρώδες', labelEn: 'Sparkling', labelKey: 'wineType.sparkling' },
  { value: 'dessert', label: 'Γλυκό', labelEn: 'Dessert', labelKey: 'wineType.dessert' },
  { value: 'fortified', label: 'Ενισχυμένο', labelEn: 'Fortified', labelKey: 'wineType.fortified' },
  { value: 'orange', label: 'Πορτοκαλί', labelEn: 'Orange', labelKey: 'wineType.orange' },
] as const;

/* ------------------------------------------------------------------ */
/*  Feed Management Types                                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Filter Facets                                                      */
/* ------------------------------------------------------------------ */

export type FacetUIType = 'checkbox' | 'slider' | 'color' | 'radio' | 'toggle';

export interface FacetOption {
  value: string;
  label: string;
  color?: string;
}

export interface FilterFacetConfig {
  id: string;
  name: string;
  field: string;
  uiType: FacetUIType;
  expanded: boolean;
  enabled: boolean;
  order: number;
  options?: FacetOption[];
}

/* ------------------------------------------------------------------ */
/*  Feeds                                                              */
/* ------------------------------------------------------------------ */

export type FeedFormat = 'json' | 'xml' | 'csv' | 'google-merchant' | 'skroutz' | 'bestprice' | 'crawl';

export type FeedType = 'products' | 'categories' | 'articles' | 'brands';

export type FeedStatus = 'active' | 'paused' | 'error';

export interface FieldMapping {
  id: string;
  name: string;
  price: string;
  category?: string;
  description?: string;
  imageUrl?: string;
  url?: string;
  inStock?: string;
  rootPath?: string;
  itemPath?: string;
  nameEl?: string;
  type?: string;
  region?: string;
  country?: string;
  grape?: string;
  producer?: string;
  vintage?: string;
  salePrice?: string;
  rating?: string;
  tags?: string;
  /** Article-specific fields */
  title?: string;
  excerpt?: string;
  content?: string;
  publishedAt?: string;
}

export interface FeedAuth {
  type: 'basic' | 'bearer' | 'apikey';
  credentials: string;
}

export interface TransformRule {
  field: string;
  operation: 'replace' | 'append' | 'prefix' | 'map' | 'split' | 'number' | 'boolean';
  params: Record<string, string>;
}

/** Configuration for web crawl-based feeds */
export interface CrawlConfig {
  startUrls: string[];
  discovery: CrawlDiscovery;
  selectors: CrawlSelectors;
  rateLimit?: {
    delayMs: number;
    maxConcurrent: number;
  };
  maxPages?: number;
  headers?: Record<string, string>;
  urlFilters?: {
    include?: string[];
    exclude?: string[];
  };
}

export interface CrawlDiscovery {
  method: 'sitemap' | 'pagination' | 'links';
  sitemapUrl?: string;
  paginationSelector?: string;
  maxPaginationPages?: number;
  productLinkSelector?: string;
  productUrlPattern?: string;
}

export interface CrawlSelectors {
  container?: string;
  id: string | CrawlSelectorConfig;
  name: string | CrawlSelectorConfig;
  price: string | CrawlSelectorConfig;
  description?: string | CrawlSelectorConfig;
  imageUrl?: string | CrawlSelectorConfig;
  url?: string | CrawlSelectorConfig;
  inStock?: string | CrawlSelectorConfig;
  category?: string | CrawlSelectorConfig;
  producer?: string | CrawlSelectorConfig;
  region?: string | CrawlSelectorConfig;
  country?: string | CrawlSelectorConfig;
  grape?: string | CrawlSelectorConfig;
  vintage?: string | CrawlSelectorConfig;
  type?: string | CrawlSelectorConfig;
  salePrice?: string | CrawlSelectorConfig;
  rating?: string | CrawlSelectorConfig;
  reviewCount?: string | CrawlSelectorConfig;
  tags?: string | CrawlSelectorConfig;
}

export interface CrawlSelectorConfig {
  selector: string;
  attribute?: string;
  regex?: string;
  regexGroup?: number;
  transform?: 'trim' | 'lowercase' | 'number' | 'boolean';
  default?: string;
  multiple?: boolean;
}

export interface FeedPagination {
  enabled: boolean;
  pageParam?: string;
  pageSizeParam?: string;
  pageSize?: number;
  totalPagesPath?: string;
  hasNextPagePath?: string;
  maxPages?: number;
}

export interface FeedConfig {
  id: string;
  name?: string;
  url: string;
  format: FeedFormat;
  type?: FeedType;
  /** Language partition this feed belongs to (e.g. 'el', 'en') */
  lang?: string;
  mapping: FieldMapping;
  crawlConfig?: CrawlConfig;
  schedule?: string;
  auth?: FeedAuth;
  transform?: TransformRule[];
  pagination?: FeedPagination;
  status?: FeedStatus;
  lastSyncAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FeedWithSchedulerStatus extends FeedConfig {
  schedulerStatus?: {
    nextRun: string | null;
    isRunning: boolean;
    retryCount: number;
  } | null;
}

export interface SyncHistoryEntry {
  id: number;
  feedId: string;
  status: 'success' | 'partial' | 'failed';
  total: number;
  parsed: number;
  skipped: number;
  errors: number;
  durationMs: number;
  errorDetails?: string;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/*  Layout Manager Types                                               */
/* ------------------------------------------------------------------ */

export type WidgetType =
  | 'banner'
  | 'trending-searches'
  | 'recent-searches'
  | 'brands'
  | 'articles'
  | 'best-seller-wines'
  | 'promoted-wines'
  | 'categories'
  | 'filters'
  | 'recently-viewed';

export type WidgetSize = 'compact' | 'default' | 'large';

export const WIDGET_SIZE_FONTS: Record<WidgetSize, FontSizeConfig> = {
  compact: { heading: 'xs', body: 'xs', small: 'xs' },
  default: { heading: 'sm', body: 'sm', small: 'xs' },
  large:   { heading: 'lg', body: 'base', small: 'sm' },
};

export const WIDGET_SIZE_PADDING: Record<WidgetSize, string> = {
  compact: 'px-3 py-1.5 gap-1',
  default: 'px-5 py-3 gap-2',
  large:   'px-6 py-4 gap-3',
};

export type TrendingDisplayVariant = 'chips' | 'numbered-list' | 'tile-row';

export interface WidgetConfig {
  id: string;
  type: WidgetType;
  title?: string;
  visible: boolean;
  maxItems?: number;
  displayMode?: 'grid' | 'list' | 'compact';
  itemsPerRow?: ItemsPerRow;
  size?: WidgetSize;
  fontSizes?: FontSizeConfig;
  /** Display variant for the trending-searches widget */
  trendingVariant?: TrendingDisplayVariant;
}

/* ------------------------------------------------------------------ */
/*  Results Layout Types                                                */
/* ------------------------------------------------------------------ */

export type ResultSectionType =
  | 'product-results'
  | 'article-results'
  | 'page-results'
  | 'ai-context'
  | 'banner-inline';

export type TileSize = 'small' | 'medium' | 'large';

/** Default items-per-row driven by tile size */
export const TILE_SIZE_ITEMS_PER_ROW: Record<TileSize, ItemsPerRow> = {
  small: 6,
  medium: 4,
  large: 3,
};

/**
 * Minimum tile width per tile size (px). Tiles never shrink below this in
 * auto-fill grids — when there isn't room for the configured itemsPerRow at
 * this width, fewer columns render instead of squishing each tile.
 */
export const TILE_SIZE_MIN_WIDTH: Record<TileSize, number> = {
  small: 140,
  medium: 180,
  large: 240,
};

/**
 * Maximum tile width per tile size (px). On wide containers the grid is
 * capped at `itemsPerRow × max` so tiles render at their natural width
 * instead of stretching to fill all available space. This makes the
 * visual difference between small/medium/large noticeable regardless of
 * viewport width.
 */
export const TILE_SIZE_MAX_WIDTH: Record<TileSize, number> = {
  small: 180,
  medium: 240,
  large: 300,
};

export interface ResultSectionConfig {
  id: string;
  type: ResultSectionType;
  visible: boolean;
  title?: string;
  maxItems?: number;
  displayMode?: 'grid' | 'list';
  itemsPerRow?: ItemsPerRow;
  tileSize?: TileSize;
}

export interface ColumnConfig {
  id: string;
  widgets: WidgetConfig[];
  widthPercent?: number;
}

export interface ResultsColumnConfig {
  id: string;
  sections: ResultSectionConfig[];
  widthPercent?: number;
}

export type FontSizeScale = 'xs' | 'sm' | 'base' | 'lg' | 'xl';

export interface FontSizeConfig {
  heading?: FontSizeScale;
  body?: FontSizeScale;
  small?: FontSizeScale;
}

export const FONT_SIZE_CLASS: Record<FontSizeScale, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
};

export type ModalMode = 'fullscreen' | 'dropdown';
export type ColumnCount = 1 | 2 | 3;

export type ItemsPerRow = 2 | 3 | 4 | 5 | 6;

export interface LayoutConfig {
  modalMode: ModalMode;
  columnCount: ColumnCount;
  fullscreenItemsPerRow: ItemsPerRow;
  dropdownItemsPerRow: ItemsPerRow;
  headerWidgets: WidgetConfig[];
  columns: ColumnConfig[];
  /** @deprecated Use resultsHeaderSections + resultsColumns instead */
  resultsLayout?: ResultSectionConfig[];
  resultsColumnCount?: ColumnCount;
  resultsHeaderSections?: ResultSectionConfig[];
  resultsColumns?: ResultsColumnConfig[];
  fontSizes?: FontSizeConfig;
  /** When true, auto-expand remaining columns to full width if a column has no content */
  autoCollapseEmptyColumns?: boolean;
  /** Width percentage of the facet/filter sidebar in search results (default 20) */
  filterColumnWidthPercent?: number;
}

export const WIDGET_META: Record<WidgetType, { label: string; labelEn: string; icon: string }> = {
  banner: { label: 'Διαφημιστικό Banner', labelEn: 'Banner', icon: '📢' },
  'trending-searches': { label: 'Trending Αναζητήσεις', labelEn: 'Trending Searches', icon: '🔥' },
  'recent-searches': { label: 'Πρόσφατες Αναζητήσεις', labelEn: 'Recent Searches', icon: '🕐' },
  brands: { label: 'Brands / Παραγωγοί', labelEn: 'Brands', icon: '🏷️' },
  articles: { label: 'Άρθρα', labelEn: 'Articles', icon: '📰' },
  'best-seller-wines': { label: 'Best Sellers', labelEn: 'Best Sellers', icon: '⭐' },
  'promoted-wines': { label: 'Προσφορές', labelEn: 'Promoted Wines', icon: '🎁' },
  categories: { label: 'Κατηγορίες', labelEn: 'Categories', icon: '🍷' },
  filters: { label: 'Φίλτρα Περιήγησης', labelEn: 'Browse Filters', icon: '🔍' },
  'recently-viewed': { label: 'Πρόσφατα Προβεβλημένα', labelEn: 'Recently Viewed', icon: '👁️' },
};

export const RESULT_SECTION_META: Record<ResultSectionType, { label: string; labelEn: string; icon: string }> = {
  'product-results': { label: 'Αποτελέσματα Προϊόντων', labelEn: 'Product Results', icon: '🍷' },
  'article-results': { label: 'Αποτελέσματα Άρθρων', labelEn: 'Article Results', icon: '📰' },
  'page-results': { label: 'Αποτελέσματα Σελίδων', labelEn: 'Page Results', icon: '📄' },
  'ai-context': { label: 'AI Σύμβουλος', labelEn: 'AI Context', icon: '🤖' },
  'banner-inline': { label: 'Inline Banner', labelEn: 'Inline Banner', icon: '📢' },
};

export const DEFAULT_RESULTS_LAYOUT: ResultSectionConfig[] = [
  { id: 'rs-ai', type: 'ai-context', visible: true },
  { id: 'rs-products', type: 'product-results', visible: true, displayMode: 'grid' },
  { id: 'rs-articles', type: 'article-results', visible: true, displayMode: 'list' },
  { id: 'rs-pages', type: 'page-results', visible: true, displayMode: 'list' },
];

export const DEFAULT_RESULTS_HEADER_SECTIONS: ResultSectionConfig[] = [
  { id: 'rs-ai', type: 'ai-context', visible: true },
];

export const DEFAULT_RESULTS_COLUMNS: ResultsColumnConfig[] = [
  {
    id: 'rcol-1',
    sections: [
      { id: 'rs-products', type: 'product-results', visible: true, displayMode: 'grid' },
      { id: 'rs-articles', type: 'article-results', visible: true, displayMode: 'list' },
      { id: 'rs-pages', type: 'page-results', visible: true, displayMode: 'list' },
    ],
  },
];

export const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  modalMode: 'fullscreen',
  columnCount: 2,
  fullscreenItemsPerRow: 4,
  dropdownItemsPerRow: 3,
  autoCollapseEmptyColumns: true,
  headerWidgets: [
    { id: 'h-trending', type: 'trending-searches', visible: true },
  ],
  columns: [
    {
      id: 'col-1',
      widgets: [
        { id: 'w-recent', type: 'recent-searches', visible: true },
      ],
    },
    {
      id: 'col-2',
      widgets: [
        { id: 'w-bestsellers', type: 'best-seller-wines', visible: true, maxItems: 6 },
        { id: 'w-promos', type: 'promoted-wines', visible: true, maxItems: 6 },
      ],
    },
  ],
};
